/**
 * Constants for Deals page
 */

import type { DealCategory, SortOption, MysteryTier, TierCosts } from '../types';
// Alias for backward compatibility
type MysteryType = MysteryTier;

// localStorage keys for persisting state
export const DEALS_STORE_KEY = 'rumio-deals-store';
export const STORAGE_KEYS = {
  COUNTRY: 'rumio-deals-country',
  STORE: 'rumio-deals-store',
};

// Category labels
export const CATEGORY_LABELS: Record<DealCategory, string> = {
  all: 'All Deals',
  last_chance: 'Last Chance',
  family_offers: 'Family Offers',
  limited_time: 'Limited Time',
  as_is: 'As-Is',
};

// Category descriptions
export const CATEGORY_DESCRIPTIONS: Record<DealCategory, string> = {
  all: 'Browse all sale products',
  last_chance: 'Clearance items at reduced prices',
  family_offers: 'Exclusive deals for IKEA Family members',
  limited_time: 'Seasonal and promotional offers',
  as_is: 'Pre-owned and floor model furniture',
};

// Sort options
export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'discount', label: 'Biggest Discount' },
];

// Mystery box tier configuration
export const MYSTERY_TIERS: Record<MysteryType, {
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  glowColor: string;
  description: string;
  ageRange: string;
}> = {
  super: {
    name: 'Super',
    icon: '⭐',
    color: 'text-tier-super',
    bgColor: 'bg-gradient-to-br from-action-light to-action-dark',
    borderColor: 'border-action-light',
    glowColor: 'var(--color-action-light)',
    description: 'Ultra-fresh deals',
    ageRange: '0-3 hours',
  },
  fresh: {
    name: 'Fresh',
    icon: '💎',
    color: 'text-purple-500',
    bgColor: 'bg-gradient-to-br from-purple-500 to-purple-700',
    borderColor: 'border-purple-400',
    glowColor: 'rgba(168, 85, 247, 0.5)',
    description: 'New arrivals',
    ageRange: '3-24 hours',
  },
  standard: {
    name: 'Standard',
    icon: '🎟️',
    color: 'text-blue-500',
    bgColor: 'bg-gradient-to-br from-blue-500 to-blue-700',
    borderColor: 'border-blue-400',
    glowColor: 'rgba(59, 130, 246, 0.5)',
    description: 'Early access deals',
    ageRange: '24-48 hours',
  },
};

// Rumio display configuration
export const RUMIO_CONFIG: Record<MysteryType, {
  name: string;
  namePlural: string;
  icon: string;
  color: string;
  bgGradient: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  description: string;
}> = {
  super: {
    name: 'Super Rumio',
    namePlural: 'Super Rumios',
    icon: '⭐',
    color: 'text-tier-super',
    bgGradient: 'from-action-light to-action-dark',
    bgColor: 'bg-action-light/20',
    textColor: 'text-action-dark',
    borderColor: 'border-action-light',
    description: 'Unlock ultra-fresh deals (0-3 hours old)',
  },
  fresh: {
    name: 'Fresh Rumio',
    namePlural: 'Fresh Rumios',
    icon: '💎',
    color: 'text-purple-500',
    bgGradient: 'from-purple-500 to-purple-700',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-300',
    description: 'Unlock new arrivals (3-24 hours old)',
  },
  standard: {
    name: 'Standard Rumio',
    namePlural: 'Standard Rumios',
    icon: '🎟️',
    color: 'text-blue-500',
    bgGradient: 'from-blue-500 to-blue-700',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-300',
    description: 'Unlock early access deals (24-48 hours old)',
  },
};

// Slot machine symbols and display
export const SLOT_SYMBOLS = {
  STANDARD: { symbol: '🎟️', color: 'text-blue-500' },
  FRESH: { symbol: '💎', color: 'text-purple-500' },
  SUPER: { symbol: '⭐', color: 'text-tier-super' },
  XP: { symbol: '✨', color: 'text-green-500' },
  MISS: { symbol: '🍋', color: 'text-star-rating' },
  PRO: { symbol: '👑', color: 'text-action-dark' },
};

// Tier visibility delays (in hours)
export const TIER_DELAY_HOURS: Record<string, number> = {
  free: 48,
  pro: 24,
  'pro+': 0,
  pro_plus: 0,
};

// Tier unlock costs (how many Rumios to unlock each tier)
export const TIER_UNLOCK_COSTS: TierCosts = {
  super: 3,    // 0-3hr old products
  fresh: 2,    // 3-24hr old products
  standard: 1, // 24-48hr old products
};

// Unified Rumio display configuration
export const UNIFIED_RUMIO_CONFIG = {
  name: 'Rumio',
  namePlural: 'Rumios',
  iconUrl: '/icons/rumio-ring.png?v=8', // Gold ring from 3D texture
  color: 'text-amber-500',
  bgGradient: 'from-amber-400 to-amber-600',
  bgColor: 'bg-amber-100',
  textColor: 'text-amber-700',
  borderColor: 'border-amber-300',
  description: 'Unlock mystery deals across all tiers',
};

// Country to language mapping for IKEA URLs
export const COUNTRY_LANGUAGES: Record<string, string> = {
  US: 'en',
  CA: 'en',
  GB: 'en',
  AU: 'en',
  DE: 'de',
  FR: 'fr',
  ES: 'es',
  IT: 'it',
  NL: 'nl',
  SE: 'sv',
  NO: 'no',
  DK: 'da',
  FI: 'fi',
  PL: 'pl',
  AT: 'de',
  CH: 'de',
  BE: 'fr',
  IE: 'en',
  PT: 'pt',
  CZ: 'cs',
};

// Items per page for infinite scroll
export const ITEMS_PER_PAGE = 24;

// Search debounce delay in ms
export const SEARCH_DEBOUNCE_MS = 300;

// Cache times for React Query
export const CACHE_TIMES = {
  COUNTRIES: 5 * 60 * 1000,     // 5 minutes
  SUMMARY: 60 * 1000,           // 1 minute
  PRODUCTS: 2 * 60 * 1000,      // 2 minutes
  STORES: 30 * 60 * 1000,       // 30 minutes
  DETECT_COUNTRY: Infinity,     // Never expires
  RUMIO_BALANCE: 30 * 1000,     // 30 seconds
  SPIN_STATUS: 30 * 1000,       // 30 seconds
  BALANCE: 30 * 1000,           // 30 seconds
  PACKS: 5 * 60 * 1000,         // 5 minutes
  UNLOCKED: 60 * 1000,          // 1 minute
  HISTORY: 5 * 60 * 1000,       // 5 minutes
  MULTIPLIERS: 5 * 60 * 1000,   // 5 minutes
  SPIN_HISTORY: 5 * 60 * 1000,  // 5 minutes
};
